//----------------------------------------------------------------------------------------------------------------------
// Event Bus
//----------------------------------------------------------------------------------------------------------------------

import type { LoggerInterface } from '../interfaces/logger.ts';
import type { Disposable } from '../interfaces/lifecycle.ts';
import type { LibraryEventPayloadMap } from '../events/payloads.ts';
import type { WildcardPattern } from '../events/types.ts';
import { LoggingUtility, SAGELogger } from '../utils/logger.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * A typed game event with compile-time payload checking.
 *
 * @template T - The event type string (e.g., 'input:changed')
 * @template TPayloadMap - The payload map to resolve types from
 */
export interface GameEvent<
    T extends string = string,
    TPayloadMap extends Record<string, unknown> = Record<string, unknown>,
>
{
    /** The string identifier/category of the event (e.g. "input:keydown"). */
    type : T;
    /** The optional ID of whoever sent this event. */
    senderID ?: string;
    /** The optional ID of who should receive this event. */
    targetID ?: string;
    /** The payload, typed based on the payload map. */
    payload : T extends keyof TPayloadMap ? TPayloadMap[T] : unknown;
}

/**
 * A callback function that receives a {@link GameEvent} with a given payload type.
 *
 * @template T - The event type string
 * @template TPayloadMap - The payload map to resolve types from
 */
export type GameEventCallback<
    T extends string = string,
    TPayloadMap extends Record<string, unknown> = Record<string, unknown>,
> = (event : GameEvent<T, TPayloadMap>) => void | Promise<void>;

/**
 * Represents a subscription record internally within the EventBus.
 *
 * We store the callback as if it handles `GameEvent<any>`
 * because we can't unify multiple different payload types in the same structure.
 */
interface Subscription
{
    /** Precompiled RegExp if this subscription is pattern-based. */
    pattern ?: RegExp;
    /** The callback, but stored to accept any payload type (due to the union nature). */
    callback : (event : GameEvent<any, any>) => void | Promise<void>;
}

/**
 * An unsubscribe function, returned by any subscribe method.
 */
export type Unsubscribe = () => void;

/**
 * Callback for wildcard/pattern subscriptions.
 * Payload is unknown since multiple event types may match.
 */
export type WildcardEventCallback
    = (event : GameEvent<string, Record<string, unknown>>) => void | Promise<void>;

//----------------------------------------------------------------------------------------------------------------------

/**
 * A high-performance, type-safe event bus that supports exact or pattern-based subscriptions.
 *
 * The generic parameter `TPayloadMap` maps event type strings to their payload types,
 * enabling compile-time checking of event/payload combinations.
 *
 * @template TPayloadMap - Map of event type strings to their payload types.
 *                         Defaults to LibraryEventPayloadMap for internal library usage.
 *
 * @example
 * // Using library events (default)
 * const bus = new GameEventBus();
 * bus.publish({ type: 'level:progress', payload: { levelName: 'Level1', progress: 50 } });
 *
 * @example
 * // User-defined events with full type safety
 * interface MyEvents {
 *     'game:score-updated': { score: number; player: string };
 *     'game:paused': { reason: string };
 *     [key: string]: unknown; // Allow other events
 * }
 *
 * const bus = new GameEventBus<MyEvents>();
 * bus.publish({ type: 'game:score-updated', payload: { score: 100, player: 'alice' } });
 *
 * @example
 * // Merging user events with library events
 * const bus = new GameEventBus<MyEvents & LibraryEventPayloadMap>();
 */
export class GameEventBus<TPayloadMap extends Record<string, unknown> = LibraryEventPayloadMap> implements Disposable
{
    /**
     * For exact event-type matches:
     * - Key = event type string (e.g. "input:keydown")
     * - Value = set of subscriptions for that exact type
     */
    private directMap = new Map<string, Set<Subscription>>();

    /**
     * For pattern-based (wildcard or RegExp) subscriptions.
     */
    private patternSubs = new Set<Subscription>();

    /**
     * Logger instance
     */
    private _log : LoggerInterface;

    /**
     * Creates a new GameEventBus instance
     *
     * @param logger
     */
    constructor(logger ?: LoggingUtility)
    {
        this._log = logger?.getLogger('EventBus') || new SAGELogger('EventBus');
    }

    //------------------------------------------------------------------------------------------------------------------
    // Subscribe
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Subscribe with automatic detection:
     *   - If `eventTypeOrPattern` is a `RegExp` or a string containing `*`, treat it as a pattern subscription.
     *   - Otherwise, treat it as an exact subscription.
     *
     * @template T - The event type string (must be a key in TPayloadMap for type safety)
     * @param eventTypeOrPattern - Exact event type string, wildcard pattern, or RegExp
     * @param callback
     * @returns A function that removes this subscription when called
     */
    public subscribe<T extends keyof TPayloadMap & string>(
        eventTypeOrPattern : T | WildcardPattern | RegExp,
        callback : GameEventCallback<T, TPayloadMap>
    ) : Unsubscribe
    {
        this._log.trace(`Subscribe request for: ${ eventTypeOrPattern.toString() }`);

        // If it's explicitly a RegExp or includes '*', treat as pattern:
        if(
            eventTypeOrPattern instanceof RegExp
            || (typeof eventTypeOrPattern === 'string' && eventTypeOrPattern.includes('*'))
        )
        {
            return this.subscribePattern(
                eventTypeOrPattern as WildcardPattern | RegExp,
                callback as WildcardEventCallback
            );
        }
        else
        {
            return this.subscribeExact(eventTypeOrPattern as T, callback);
        }
    }

    /**
     * Subscribes to an exact event type (e.g. "input:keydown").
     *
     * @template T - The event type string (must be a key in TPayloadMap for type safety)
     * @param eventType
     * @param callback
     * @returns A function that removes this subscription when called
     */
    public subscribeExact<T extends keyof TPayloadMap & string>(
        eventType : T,
        callback : GameEventCallback<T, TPayloadMap>
    ) : Unsubscribe
    {
        this._log.debug(`Adding exact subscription for: ${ eventType }`);

        let subs = this.directMap.get(eventType);
        if(!subs)
        {
            subs = new Set();
            this.directMap.set(eventType, subs);
        }

        // Store as a Subscription that accepts `GameEvent<any>`
        const subscription : Subscription = {
            callback: (event) => callback(event as GameEvent<T, TPayloadMap>),
        };
        subs.add(subscription);

        return () =>
        {
            this._log.debug(`Removing exact subscription for: ${ eventType }`);
            subs.delete(subscription);
            if(subs.size === 0)
            {
                this.directMap.delete(eventType);
            }
        };
    }

    /**
     * Subscribes with either:
     *  - a wildcard string (contains `*`) that gets converted to a RegExp
     *  - a RegExp directly
     *
     * Note: Payload typing is not available for wildcards since multiple event types may match.
     *
     * @param patternOrRegExp - Wildcard string (e.g. `"input:*"`) or a RegExp
     * @param callback
     * @returns A function that removes this subscription when called
     */
    public subscribePattern(
        patternOrRegExp : WildcardPattern | RegExp,
        callback : WildcardEventCallback
    ) : Unsubscribe
    {
        let regex : RegExp;

        if(typeof patternOrRegExp === 'string')
        {
            // We replace '*' with '.*' for wildcard matching
            const escaped = patternOrRegExp
                .replace(/[-/\\^$+?.()|[\]{}]/g, '\\$&') // escape special chars
                .replace(/\*/g, '.*'); // replace '*' with '.*'

            // e.g. "input:*" => "input:.*" => new RegExp("^input:.*$")
            regex = new RegExp(`^${ escaped }$`);
            this._log.debug(
                `Adding pattern subscription for string: ${ patternOrRegExp }, regex: ${ regex.toString() }`
            );
        }
        else
        {
            // If already a RegExp, use as-is
            regex = patternOrRegExp;
            this._log.debug(`Adding pattern subscription for regex: ${ regex.toString() }`);
        }

        const subscription : Subscription = {
            pattern: regex,
            callback: (event) => callback(event as GameEvent<string, Record<string, unknown>>),
        };
        this.patternSubs.add(subscription);

        return () =>
        {
            this._log.debug(`Removing pattern subscription: ${ regex.toString() }`);
            this.patternSubs.delete(subscription);
        };
    }

    //------------------------------------------------------------------------------------------------------------------
    // Publish
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Publishes an event, invoking all matching callbacks.
     *
     * **Async Execution Model:**
     * Callbacks are invoked asynchronously via the microtask queue using `Promise.resolve().then()`.
     * This means `publish()` returns immediately, before any callbacks execute. This design:
     * - Prevents stack overflow from recursive event publishing
     * - Allows multiple events to be queued before processing
     * - Is ideal for fire-and-forget events (notifications, state changes)
     *
     * **Not suitable for request-response patterns.** If you need to wait for a callback's
     * result, consider using a different communication mechanism.
     *
     * @template T - The event type string (must be a key in TPayloadMap for type safety)
     * @param event
     */
    public publish<T extends keyof TPayloadMap & string>(
        event : GameEvent<T, TPayloadMap>
    ) : void
    {
        this._log.trace(`Publishing event: ${ event.type }`, event);

        let callbackCount = 0;

        // 1) Exact match
        const directSubs = this.directMap.get(event.type);
        if(directSubs)
        {
            callbackCount += directSubs.size;
            for(const sub of directSubs)
            {
                Promise.resolve()
                    .then(() => sub.callback(event))
                    .catch((err) =>
                    {
                        this._log.error(`Error in event handler for '${ event.type }':`, err);
                    });
            }
        }

        // 2) Pattern subscriptions
        for(const sub of this.patternSubs)
        {
            if(sub.pattern && sub.pattern.test(event.type))
            {
                callbackCount++;
                Promise.resolve()
                    .then(() => sub.callback(event))
                    .catch((err) =>
                    {
                        this._log.error(`Error in pattern event handler for '${ event.type }':`, err);
                    });
            }
        }

        if(callbackCount === 0)
        {
            this._log.debug(`No subscribers found for event: ${ event.type }`);
        }
        else
        {
            this._log.trace(`Event ${ event.type } dispatched to ${ callbackCount } subscribers`);
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Lifecycle
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Tears down the event bus and clears all subscriptions.
     * After calling this method, no events will be delivered.
     */
    public async $teardown() : Promise<void>
    {
        this._log.debug('Tearing down EventBus');
        this.directMap.clear();
        this.patternSubs.clear();
    }
}

//----------------------------------------------------------------------------------------------------------------------
